import React, { useMemo, useState } from "react";
import { ArrowLeft, Search, X, Users, Check, UsersRound } from "lucide-react";
import Avatar from "./Avatar";
import { useChat } from "./chatStore";
import styles from "./chat.module.scss";

const KIND_LABEL = { user: "Kişiler", client: "Müşteriler", lead: "Adaylar" };
const KIND_ORDER = ["user", "client", "lead"];

const groupContacts = (contacts, q) => {
  const k = q.trim().toLowerCase();
  const filtered = k
    ? contacts.filter((c) =>
        [c.name, c.title, c.company].filter(Boolean).join(" ").toLowerCase().includes(k)
      )
    : contacts;
  const byKind = {};
  filtered.forEach((c) => {
    (byKind[c.kind] = byKind[c.kind] || []).push(c);
  });
  return KIND_ORDER.filter((kind) => byKind[kind]?.length).map((kind) => ({
    kind,
    label: KIND_LABEL[kind],
    items: byKind[kind].sort((a, b) => a.name.localeCompare(b.name, "tr")),
  }));
};

const contactColor = (kind) =>
  kind === "client" ? "#f59e0b" : kind === "lead" ? "#0ea5e9" : "#6366f1";

// Yeni sohbet — kişi seç (anında DM) VEYA yeni grup oluştur (isim + üye seç).
const NewChat = ({ onBack }) => {
  const { contacts, startWith, createGroup } = useChat();
  const [mode, setMode] = useState("pick"); // 'pick' | 'group'
  const [q, setQ] = useState("");
  const [groupName, setGroupName] = useState("");
  const [selected, setSelected] = useState(() => new Set());

  const groups = useMemo(() => groupContacts(contacts, q), [contacts, q]);

  const toggleMember = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const submitGroup = () => {
    if (selected.size === 0) return;
    createGroup({ name: groupName, memberIds: [...selected] });
  };

  const isGroupMode = mode === "group";

  return (
    <div className={styles.newPane}>
      <header className={styles.viewHeader}>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={isGroupMode ? () => setMode("pick") : onBack}
          aria-label="Geri"
        >
          <ArrowLeft size={20} />
        </button>
        <div className={styles.viewHeaderMeta}>
          <span className={styles.viewHeaderName}>
            {isGroupMode ? "Yeni grup" : "Yeni sohbet"}
          </span>
          <span className={styles.viewHeaderStatus}>
            {isGroupMode
              ? selected.size > 0
                ? `${selected.size} üye seçildi`
                : "Üyeleri seç"
              : "Bir kişi seç"}
          </span>
        </div>
      </header>

      {isGroupMode && (
        <div className={styles.groupNameRow}>
          <span className={styles.groupNameIcon}>
            <UsersRound size={18} />
          </span>
          <input
            className={styles.searchInput}
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Grup adı"
            type="text"
            autoFocus
          />
        </div>
      )}

      <div className={styles.searchWrap}>
        <Search size={16} className={styles.searchIcon} />
        <input
          className={styles.searchInput}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={isGroupMode ? "Üye ara" : "İsim, ünvan veya şirket ara"}
          type="text"
        />
        {q && (
          <button type="button" className={styles.searchClear} onClick={() => setQ("")} aria-label="Temizle">
            <X size={14} />
          </button>
        )}
      </div>

      {!isGroupMode && (
        <button type="button" className={styles.newGroupCta} onClick={() => setMode("group")}>
          <span className={styles.newGroupIcon}>
            <Users size={20} />
          </span>
          <span className={styles.newGroupLabel}>Yeni grup oluştur</span>
        </button>
      )}

      <div className={styles.convScroll}>
        {groups.length === 0 ? (
          <div className={styles.listEmpty}>
            <p>Kişi bulunamadı.</p>
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.kind} className={styles.contactGroup}>
              <div className={styles.contactGroupLabel}>{g.label}</div>
              {g.items.map((c) => {
                const checked = selected.has(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`${styles.contactRow} ${isGroupMode && checked ? styles.contactRowChecked : ""}`}
                    onClick={() => (isGroupMode ? toggleMember(c.id) : startWith(c.id))}
                  >
                    <Avatar src={c.avatar} name={c.name} color={contactColor(c.kind)} size={44} />
                    <span className={styles.contactBody}>
                      <span className={styles.contactName}>{c.name}</span>
                      <span className={styles.contactTitle}>
                        {[c.title, c.company].filter(Boolean).join(" · ") || "—"}
                      </span>
                    </span>
                    {isGroupMode ? (
                      <span className={`${styles.checkbox} ${checked ? styles.checkboxOn : ""}`}>
                        {checked && <Check size={13} />}
                      </span>
                    ) : (
                      c.kind !== "user" && (
                        <span
                          className={`${styles.contactBadge} ${
                            c.kind === "client" ? styles.badgeClient : styles.badgeLead
                          }`}
                        >
                          {c.kind === "client" ? "Müşteri" : "Aday"}
                        </span>
                      )
                    )}
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>

      {isGroupMode && (
        <footer className={styles.groupFooter}>
          <button
            type="button"
            className={styles.groupCreateBtn}
            onClick={submitGroup}
            disabled={selected.size === 0}
          >
            Grubu oluştur{selected.size > 0 ? ` (${selected.size})` : ""}
          </button>
        </footer>
      )}
    </div>
  );
};

export default NewChat;
